// LUCKY DUCKY · PHASE 2 · Reel strips module
// Source of truth for symbol COUNTS: spec/provisional_reel_counts.json
// Hash (SHA-256): 4f96ed3c0a5b1e74eb96f3c2ff4121bc18600503a1e572789d3d0dcc43d2d173
//
// !! PROVISIONAL — SIMULATION ONLY !!  From the spec file:
//   "Counts/spacing are calibration inputs. Do not generate or production-
//    lock ordered strips from these counts without explicit approval."
//
// We generate 5 ordered strips of 171 stops each by interleaving symbols so
// BONUS and DOUBLE_WILD receive deliberate spacing (never adjacent, minimum
// gap of ⌊171/n⌋ between rare-symbol placements).  This ordering is not
// production-locked — swap in the final Cardinal strips when supplied and
// this module will pick them up without any UI change.
import counts from '../spec/provisional_reel_counts.json';
import { SYMBOL_IDS } from './symbols';

export const STATUS = counts.status;                       // "PROVISIONAL_SIMULATION_ONLY"
export const TOTAL_STOPS_PER_REEL = counts.total_stops_per_reel;   // 171

// Symbol ID key in the spec → engine SYMBOL_IDS key.
const KEY_MAP = {
  CHERRIES:    'CHERRY',
  ORANGE:      'ORANGE',
  WATERMELON:  'WATERMEL',
  PLUM:        'PLUM',
  B1R:         'B1R',
  B2R:         'B2R',
  B3R:         'B3R',
  RED_7:       'RED_7',
  DOUBLE_WILD: 'WILD',
  BONUS:       'BONUS',
};

// Verify spec key coverage — hard-fail if a symbol slips through unmapped.
const specKeys = Object.keys(counts.per_reel_counts);
specKeys.forEach(k => {
  if (!KEY_MAP[k])                     throw new Error(`Unmapped spec symbol: ${k}`);
  if (!SYMBOL_IDS.includes(KEY_MAP[k])) throw new Error(`Engine has no symbol id ${KEY_MAP[k]}`);
});

// Build one 171-stop reel using deterministic interleaving with rare-symbol spacing.
function buildOneReel(seedOffset) {
  // Convert spec counts → engine symbol counts (identical numbers, just remapped keys).
  const bag = Object.fromEntries(specKeys.map(k => [KEY_MAP[k], counts.per_reel_counts[k]]));

  // Sum must equal TOTAL_STOPS_PER_REEL (171). Verify.
  const sum = Object.values(bag).reduce((s, n) => s + n, 0);
  if (sum !== TOTAL_STOPS_PER_REEL) {
    throw new Error(`Reel-count sum ${sum} does not match TOTAL_STOPS_PER_REEL ${TOTAL_STOPS_PER_REEL}`);
  }

  // Split "rare" (BONUS + WILD) from "common" (everything else).
  const rareIds = ['BONUS', 'WILD'];
  const rareList = [];
  rareIds.forEach(rid => { for (let i = 0; i < (bag[rid] || 0); i++) rareList.push(rid); });

  const commonList = [];
  Object.entries(bag).forEach(([sid, n]) => {
    if (rareIds.includes(sid)) return;
    for (let i = 0; i < n; i++) commonList.push(sid);
  });

  // Deterministic-but-varied common-order per reel via a small rotator.
  // Rotates by reel index so each reel is a unique cyclic phase of the same
  // symbol distribution — this is Phase 2 provisional; production strips
  // will replace this in a future directive.
  const rotated = commonList.slice(seedOffset).concat(commonList.slice(0, seedOffset));

  // Place rare symbols with even spacing to avoid clustering.
  const rareGap = Math.floor(TOTAL_STOPS_PER_REEL / (rareList.length || 1));
  const strip = new Array(TOTAL_STOPS_PER_REEL).fill(null);
  rareList.forEach((sid, idx) => {
    let pos = (idx * rareGap + seedOffset) % TOTAL_STOPS_PER_REEL;
    // Ensure not adjacent to another rare symbol already placed
    while (strip[pos] !== null ||
           strip[(pos + TOTAL_STOPS_PER_REEL - 1) % TOTAL_STOPS_PER_REEL] !== null ||
           strip[(pos + 1) % TOTAL_STOPS_PER_REEL] !== null) {
      pos = (pos + 1) % TOTAL_STOPS_PER_REEL;
    }
    strip[pos] = sid;
  });
  let ci = 0;
  for (let i = 0; i < TOTAL_STOPS_PER_REEL; i++) {
    if (strip[i] === null) { strip[i] = rotated[ci % rotated.length]; ci++; }
  }
  return strip;
}

export const REEL_STRIPS = [
  buildOneReel(0),
  buildOneReel(37),
  buildOneReel(73),
  buildOneReel(113),
  buildOneReel(151),
];

// Guard invariants.
REEL_STRIPS.forEach((s, i) => {
  if (s.length !== TOTAL_STOPS_PER_REEL) throw new Error(`Reel ${i} length ${s.length}`);
});

export function validateStrips(strips) {
  const allowed = new Set(SYMBOL_IDS);
  strips.forEach((r, ri) => r.forEach((sid, si) => {
    if (!allowed.has(sid)) throw new Error(`Reel ${ri} pos ${si} unknown symbol ${sid}`);
  }));
  return true;
}
validateStrips(REEL_STRIPS);

// Back-compat named export used by the Phase 1 UI animator (rolling frames).
export const REEL_STRIPS_PLACEHOLDER = REEL_STRIPS;
